import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { Observable, shareReplay } from 'rxjs';
import { ThemeSelectors } from '@app-core/enums/themes.enum';

export interface SettingsProps {
  theme: ThemeSelectors | null;
  prefersDark: boolean;
}

const initialProps: SettingsProps = {
  theme: null,
  prefersDark: true,
};

const STORE_NAME = 'sandoz-v2-settings';

@Injectable({ providedIn: 'root' })
export class SettingsRepository {
  private readonly store;

  public readonly theme$: Observable<'sandoz-light' | 'sandoz-dark' | null>;
  public readonly prefersDark$: Observable<boolean>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<SettingsProps>(initialProps),
    );

    persistState(this.store, {
      key: STORE_NAME,
      storage: localStorageStrategy,
    });

    this.theme$ = this.store.pipe(
      select(state => state.theme),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.prefersDark$ = this.store.pipe(
      select(state => state.prefersDark),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  /**
   * Gets a prop from the store.
   *
   * @param key
   */
  getStoreProp<K extends keyof SettingsProps>(key: K): SettingsProps[K] {
    return this.store.getValue()[key];
  }

  setStoreProp<K extends keyof SettingsProps, V extends SettingsProps[K]>(
    key: K,
    value: V,
  ): void {
    this.store.update(setProp(key, value));
  }
}
